<template>
  <base-form
    ref="formRef"
    class="f-col"
    data-test-id="form-newsletter"
    :form
    name="newsletter"
    @submit="submit"
  >
    <template v-if="$feature.newsletterFormDynamic && (auth.pending || user.pending)">
      <div class="h-6 skeleton" />
      <div v-if="$t.newsletterSubheading" class="mt-2 h-5 skeleton" />
      <div class="mt-4 h-11 skeleton" />
      <div class="mt-6 h-10 skeleton" />
    </template>
    <template v-else>
      <h2
        v-if="!hideHeading"
        class="title-3 md:text-center !subtitle-3"
        :class="classHeading"
        data-test-id="form-newsletter-title"
      >
        {{
          !notification.type && !loading && optIn
            ? $t.newsletterSuccess
            : $t.newsletterHeading
        }}
      </h2>
      <p
        v-if="!hideSubheading && $t.newsletterSubheading"
        class="mt-2 md:text-center "
        :class="classSubheading"
        data-test-id="form-newsletter-subtitle"
      >
        {{ $t.newsletterSubheading }}
      </p>

      <vf-notification
        v-if="notification.type"
        class="mt-4"
        :dismissible="notificationsDismissible"
        :type="notification.type"
        @close="dismissNotification"
      >
        {{ notification.message }}
      </vf-notification>

      <vf-form-field
        v-slot="{ attrs }"
        :hint
        name="email"
        :rule="[validateRequired($t.newsletterEmailAddress), validateEmail()]"
        :variant
      >
        <input-email
          v-model="form.email"
          v-bind="attrs"
          class="mt-4"
          :class="variant ? 'c-white' : 'c-grey-20'"
          :disabled="loading || !!email"
          :hide-validation-icons="showInlineSubmitButton && !optIn"
          :label="$t.newsletterEmailAddress"
          :variant
        >
          <template v-if="showInlineSubmitButton && !optIn" #after>
            <vf-button
              :aria-label="$t.newsletterSubmit"
              :class="classSubmit"
              :loading
              size="xs"
              type="submit"
              :variant="variant ? 'primary-inverse' : 'primary'"
            >
              <vf-icon dir="right" name="arrow" size="md" />
            </vf-button>
          </template>
        </input-email>
      </vf-form-field>

      <div class="mt-6 md:mt-4">
        <vf-form-field
          v-if="!optIn"
          v-slot="{ attrs }"
          name="policy"
          :rule="validateRequired('', $t.validators.requiredCheckbox)"
          :variant
        >
          <vf-checkbox
            v-model="form.policy"
            v-bind="attrs"
            :disabled="loading"
            required
            :size="checkboxSize"
            :variant
          >
            <span  :class="classDisclaimer" data-test-id="form-newsletter-text">
              <form-legal-disclaimer
                :content="$t.newsletterPolicy"
                :data-test-ids="{ 'brand-terms': 'form-newsletter-link', 'privacy': 'form-newsletter-link' }"
              />
            </span>
          </vf-checkbox>
        </vf-form-field>
      </div>

      <vf-button
        v-if="!optIn && !showInlineSubmitButton"
        class="mt-6 md:mt-4 md:self-center"
        :class="classSubmit"
        :disabled="loading"
        :loading
        :size="buttonSize"
        type="submit"
        :variant="variant ? 'primary-inverse' : 'primary'"
      >
        <slot name="submitLabel">
          {{ $t.newsletterSubmit }}
        </slot>
      </vf-button>
    </template>
  </base-form>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { CSSClass } from '#types/common'
import type { FormLocation } from '#types/gtm'
import type { BaseNotification } from '#types/notification'

const { formLocation = 'modal:single:none', showHint = true, campaignName } = defineProps<{
  campaignName?: string
  classDisclaimer?: CSSClass
  classHeading?: CSSClass
  classSubheading?: CSSClass
  classSubmit?: CSSClass
  formLocation?: FormLocation
  notificationsDismissible?: boolean
  showHint?: boolean
  variant?: 'inverse'
  hideHeading?: boolean
  hideSubheading?: boolean
}>()

const { checkboxSize, buttonSize, showInlineSubmitButton } = useAppConfig().components.vf.footer.newsletter
const { validateEmail, validateRequired } = useLocalizedValidators()
const { getUserEventData } = useAnalytics()
const { $gtm, $t, $optimizely, $feature } = useNuxtApp()
const subscriptions = useSubscriptions()
const user = useUserStore()
const auth = useAuthStore()
const route = useRoute()
const formRef = ref<InstanceType<typeof BaseFormType>>()

const optIn = computed(() => $feature.newsletterFormDynamic && subscriptions.newsletterSubscribed.value)
const email = computed(() => $feature.newsletterFormDynamic && user.details?.email)

const form = reactive({
  email: '',
  policy: false
})

const hint = computed(() => showHint ? $t.emailFormatHint : '')

const notification = reactive<BaseNotification>({
  type: '',
  message: ''
})

const loading = ref(false)

onMounted(() => $gtm.queueEvent('newsletterForm.onImpression', formLocation, undefined, campaignName))

const dismissNotification = () => {
  notification.type = ''
  notification.message = ''
}

watch(() => route.path, () => dismissNotification())

onActivated(reset)
watchEffect(reset)

async function submit() {
  try {
    loading.value = true
    notification.type = ''
    $gtm.push('newsletterForm.onSubmit', formLocation, undefined, campaignName)
    const consumerId = await subscriptions.newsletter({
      optIn: true,
      campaignName: campaignName || route.path,
      ...(!email.value && { email: form.email, acquisitionType: 'Signup' }),
    })

    const userEventData = await getUserEventData({ email: form.email }, consumerId)
    $gtm.push('user.onLoadUserData', userEventData)
    $optimizely?.dataReady(userEventData)

    form.policy = false
    form.email = email.value || ''

    notification.type = 'success'
    notification.message = $t.newsletterSuccess

    $gtm.push('newsletterForm.onConfirmed', formLocation, campaignName)
  }
  catch (err) {
    if (isError(err)) {
      notification.type = 'error'
      notification.message = err.message
    }
  }
  finally {
    loading.value = false
  }
}

function reset() {
  form.email = email.value || ''
  formRef.value?.reset()
  dismissNotification()
}
</script>
